import React, { useMemo, useState } from 'react';
import { Box, Drawer, alpha, Typography, IconButton } from '@mui/material';
import { Close, NotificationsActiveRounded } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import MobileNav from './MobileNav';
import { buildNavigation, metaForPath } from '../../navigation';
import { palette } from '../../theme';

/**
 * Application shell: ambient background, floating sidebar, top bar, content
 * canvas and mobile navigation.
 */
export default function AppShell({ user, onLogout, children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const location = useLocation();
  const { primary, secondary } = useMemo(() => buildNavigation(user?.role), [user?.role]);
  const meta = metaForPath(location.pathname);

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', display: 'flex' }}>
      {/* ambient background */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: -2, background: palette.background }} />
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          background: `
            radial-gradient(900px 600px at 12% -8%, ${alpha(palette.primary, 0.16)}, transparent 62%),
            radial-gradient(760px 520px at 92% 4%, ${alpha(palette.secondary, 0.13)}, transparent 60%),
            radial-gradient(1100px 800px at 50% 110%, ${alpha(palette.primary, 0.08)}, transparent 62%)
          `,
        }}
      />
      <Box
        className="holo-grid"
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          opacity: 0.55,
          maskImage: 'radial-gradient(circle at 50% 30%, black 0%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 30%, black 0%, transparent 78%)',
          pointerEvents: 'none',
        }}
      />

      <Sidebar
        items={primary}
        secondary={secondary}
        collapsed={collapsed}
        onToggle={() => setCollapsed((current) => !current)}
        role={user?.role}
      />

      {/* mobile drawer */}
      <Drawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 268,
            background: `linear-gradient(180deg, ${alpha('#0C1428', 0.98)}, ${alpha('#050816', 0.99)})`,
            borderRight: `1px solid ${alpha(palette.primary, 0.14)}`,
            backdropFilter: 'blur(24px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.25, py: 2 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.14em' }}>BLUE NODE</Typography>
          <IconButton size="small" onClick={() => setMobileMenuOpen(false)} sx={{ color: 'text.secondary' }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ px: 1.5, overflowY: 'auto' }}>
          {[...primary, ...secondary].map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <Box
                key={item.key}
                component="a"
                href={item.path}
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.75,
                  minHeight: 46,
                  mb: 0.75,
                  borderRadius: 2.5,
                  textDecoration: 'none',
                  color: active ? palette.text : 'text.secondary',
                  background: active ? alpha(palette.primary, 0.12) : 'transparent',
                  border: `1px solid ${active ? alpha(palette.primary, 0.26) : 'transparent'}`,
                }}
              >
                <Icon sx={{ fontSize: 19, color: active ? palette.primary : 'inherit' }} />
                <Typography sx={{ fontSize: '0.83rem', fontWeight: active ? 700 : 500 }}>{item.label}</Typography>
              </Box>
            );
          })}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          ml: { lg: collapsed ? '96px' : '268px' },
          transition: 'margin-left 320ms cubic-bezier(0.22,0.9,0.28,1)',
          pb: { xs: 11, lg: 0 },
        }}
      >
        <TopBar
          user={user}
          title={meta.title}
          subtitle={meta.subtitle}
          onToggleSidebar={() => setMobileMenuOpen(true)}
          onLogout={onLogout}
        />

        <Box sx={{ flex: 1, px: { xs: 1.75, sm: 2.5, md: 3.5 }, py: { xs: 2, md: 3 } }}>{children}</Box>
      </Box>

      <MobileNav
        items={primary}
        action={{
          path: '/alerts',
          label: 'Alerts',
          icon: <NotificationsActiveRounded sx={{ fontSize: 22 }} />,
        }}
      />
    </Box>
  );
}
